/**
 * VehicleFuelCardsTab - Visar alla bränslekort kopplade till ett fordon (historik + aktiva)
 * Används i VehicleCard under fliken "Bränslekort"
 * Stödjer att koppla nya kort (med bränslebolag) och koppla bort befintliga.
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from '@/store/languageStore';
import { PlusIcon } from '@heroicons/react/24/outline';
import * as fuelApi from '@/services/fuel/fuelManagementService';
import type { CardHistoryEntry } from '@/services/fuel/fuelManagementService';
import FuelCompanyIcon, { fuelCompanies } from '@/components/common/FuelCompanyIcon';
import { isOkq8FuelCompany, looksLikeOkq8EmbossedNumber } from '@/utils/okq8FuelCard';

import DateInput from '@/components/common/DateInput';

interface VehicleFuelCardsTabProps {
  vehicleId: number;
}

export default function VehicleFuelCardsTab({ vehicleId }: VehicleFuelCardsTabProps) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [cards, setCards] = useState<CardHistoryEntry[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newCardNumber, setNewCardNumber] = useState('');
  const [newCardName, setNewCardName] = useState('');
  const [newValidFrom, setNewValidFrom] = useState(() => new Date().toISOString().slice(0, 10));
  const [newFuelCompanyId, setNewFuelCompanyId] = useState<number | undefined>(undefined);
  const [newPinCode, setNewPinCode] = useState('');
  const [newEmbossedNumber, setNewEmbossedNumber] = useState('');
  const [isLinking, setIsLinking] = useState(false);
  const [isUnlinking, setIsUnlinking] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const loadCards = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    fuelApi.getVehicleCards(vehicleId)
      .then(data => { if (!cancelled) setCards(data); })
      .catch(() => { if (!cancelled) setLoadError(t('fuel.vehicleCard.loadError')); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [vehicleId, t]);

  useEffect(() => {
    return loadCards();
  }, [loadCards]);

  const handleLinkCard = async () => {
    if (!newCardNumber.trim()) return;
    if (isOkq8FuelCompany(newFuelCompanyId) && !newEmbossedNumber.trim()) {
      setFeedback({ type: 'error', message: t('fuel.vehicleCard.okq8BothRequired') });
      return;
    }
    setIsLinking(true);
    setFeedback(null);
    try {
      await fuelApi.linkCard(
        newCardNumber.trim(),
        vehicleId,
        newValidFrom,
        newFuelCompanyId,
        newPinCode || undefined,
        newCardName.trim() || undefined,
        newEmbossedNumber.trim() || undefined
      );
      setFeedback({ type: 'success', message: t('fuel.vehicleCard.linkSuccess') });
      setNewCardNumber('');
      setNewCardName('');
      setNewFuelCompanyId(undefined);
      setNewPinCode('');
      setNewEmbossedNumber('');
      setShowAddForm(false);
      loadCards();
    } catch {
      setFeedback({ type: 'error', message: t('fuel.vehicleCard.linkError') });
    } finally {
      setIsLinking(false);
    }
  };

  const handleUnlinkCard = async (cardNumber: string) => {
    if (!confirm(t('fuel.vehicleCard.confirmUnlink'))) return;
    setIsUnlinking(cardNumber);
    setFeedback(null);
    try {
      await fuelApi.unlinkCard(cardNumber);
      setFeedback({ type: 'success', message: t('fuel.vehicleCard.unlinkSuccess') });
      loadCards();
    } catch {
      setFeedback({ type: 'error', message: t('fuel.vehicleCard.unlinkError') });
    } finally {
      setIsUnlinking(null);
    }
  };

  if (loading) {
    return <p className="text-textSecondary text-center py-8 text-sm">{t('common.loading')}</p>;
  }

  const activeCards = cards.filter(c => c.is_active);
  const historicCards = cards.filter(c => !c.is_active);

  return (
    <div className="space-y-4 text-textDefault">
      {feedback && (
        <div className={`px-3 py-2 rounded text-sm ${feedback.type === 'success' ? 'bg-green-50 dark:bg-green-950/30 text-green-700 dark:text-green-200 border border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-200 border border-red-200 dark:border-red-800'}`}>
          {feedback.message}
        </div>
      )}

      <div className="flex justify-end">
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-white text-sm rounded hover:bg-primary/90 transition-colors"
        >
          <PlusIcon className="w-4 h-4" />
          {t('fuel.vehicleCard.addCard')}
        </button>
      </div>

      {showAddForm && (
        <div className="bg-bgBase border border-border rounded-lg p-4 space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs text-textSecondary mb-1">
                {isOkq8FuelCompany(newFuelCompanyId) ? t('fuel.vehicleCard.invoiceNumber') : t('fuel.vehicleCard.cardNumber')}
              </label>
              <input
                type="text"
                value={newCardNumber}
                onChange={(e) => setNewCardNumber(e.target.value)}
                placeholder={isOkq8FuelCompany(newFuelCompanyId) ? '50470950215' : 'XXXX XXXX XXXX XXXX'}
                className="w-full px-3 py-1.5 border border-border rounded text-sm bg-bgElevated text-textDefault focus:ring-1 focus:ring-primary focus:border-primary"
                autoFocus
              />
            </div>
            {isOkq8FuelCompany(newFuelCompanyId) && (
              <div>
                <label className="block text-xs text-textSecondary mb-1">{t('fuel.vehicleCard.embossedNumber')}</label>
                <input
                  type="text"
                  value={newEmbossedNumber}
                  onChange={(e) => setNewEmbossedNumber(e.target.value)}
                  placeholder="701475XXXXXXXXXX"
                  className="w-full px-3 py-1.5 border border-border rounded text-sm bg-bgElevated text-textDefault font-mono focus:ring-1 focus:ring-primary focus:border-primary"
                />
              </div>
            )}
            <div>
              <label className="block text-xs text-textSecondary mb-1">{t('fuel.cards.cardName')}</label>
              <input
                type="text"
                maxLength={50}
                value={newCardName}
                onChange={(e) => setNewCardName(e.target.value)}
                className="w-full px-3 py-1.5 border border-border rounded text-sm bg-bgElevated text-textDefault focus:ring-1 focus:ring-primary focus:border-primary"
              />
            </div>
            <div>
              <label className="block text-xs text-textSecondary mb-1">{t('fuel.cards.fuelCompany')}</label>
              <select
                value={newFuelCompanyId ?? ''}
                onChange={(e) => {
                  const nextId = e.target.value ? Number(e.target.value) : undefined;
                  setNewFuelCompanyId(nextId);
                  if (isOkq8FuelCompany(nextId) && looksLikeOkq8EmbossedNumber(newCardNumber) && !newEmbossedNumber) {
                    setNewEmbossedNumber(newCardNumber.replace(/\s+/g, ''));
                    setNewCardNumber('');
                  }
                }}
                className="w-full px-3 py-1.5 border border-border rounded text-sm focus:ring-1 focus:ring-primary focus:border-primary bg-bgElevated text-textDefault"
              >
                <option value="">-- {t('fuel.cards.fuelCompany')} --</option>
                {fuelCompanies.map(fc => (
                  <option key={fc.id} value={fc.id}>{fc.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-textSecondary mb-1">{t('fuel.vehicleCard.validFrom')}</label>
              <DateInput value={newValidFrom}
                onChange={(e) => setNewValidFrom(e.target.value)}
                className="w-full px-3 py-1.5 border border-border rounded text-sm bg-bgElevated text-textDefault focus:ring-1 focus:ring-primary focus:border-primary"
              />
            </div>
            <div>
              <label className="block text-xs text-textSecondary mb-1">{t('fuel.cards.pinCode')}</label>
              <input
                type="text"
                maxLength={20}
                value={newPinCode}
                onChange={(e) => setNewPinCode(e.target.value)}
                placeholder={t('fuel.cards.linkModal.pinCodePlaceholder')}
                className="w-full px-3 py-1.5 border border-border rounded text-sm bg-bgElevated text-textDefault font-mono focus:ring-1 focus:ring-primary focus:border-primary"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleLinkCard}
              disabled={
                isLinking ||
                !newCardNumber.trim() ||
                (isOkq8FuelCompany(newFuelCompanyId) && !newEmbossedNumber.trim())
              }
              className="px-4 py-1.5 bg-green-600 text-white text-sm rounded hover:bg-green-700 disabled:opacity-50 transition-colors"
            >
              {isLinking ? t('common.saving') : t('common.save')}
            </button>
            <button
              onClick={() => { setShowAddForm(false); setNewCardNumber(''); setNewCardName(''); setNewFuelCompanyId(undefined); setNewPinCode(''); setNewEmbossedNumber(''); }}
              className="px-4 py-1.5 border border-border text-sm rounded hover:bg-bgLight text-textDefault transition-colors"
            >
              {t('common.cancel')}
            </button>
          </div>
        </div>
      )}

      {loadError ? (
        <div className="text-center py-8 text-red-600 dark:text-red-400">
          <p className="text-sm">{loadError}</p>
        </div>
      ) : cards.length === 0 && !showAddForm ? (
        <div className="text-center py-8 text-textSecondary">
          <p className="text-sm">{t('fuel.vehicleCard.noCards')}</p>
        </div>
      ) : cards.length > 0 && (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-textSecondary text-xs border-b border-border">
              <th className="px-3 py-2">{t('fuel.cards.fuelCompany')}</th>
              <th className="px-3 py-2">{t('fuel.cards.cardName')}</th>
              <th className="px-3 py-2">{t('fuel.cards.cardNumber')}</th>
              <th className="px-3 py-2">{t('fuel.cards.embossedNumber')}</th>
              <th className="px-3 py-2">{t('fuel.cards.pinCode')}</th>
              <th className="px-3 py-2">{t('fuel.cards.history.from')}</th>
              <th className="px-3 py-2">{t('fuel.cards.history.to')}</th>
              <th className="px-3 py-2">{t('common.status')}</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {activeCards.map(card => (
              <tr key={card.id} className="border-b border-border">
                <td className="px-3 py-2">
                  {card.fuel_company_name ? (
                    <FuelCompanyIcon companyName={card.fuel_company_name} size="sm" showName />
                  ) : (
                    <span className="text-textTertiary">-</span>
                  )}
                </td>
                <td className="px-3 py-2">{card.card_name || '-'}</td>
                <td className="px-3 py-2 font-mono font-medium">{card.card_number}</td>
                <td className="px-3 py-2 font-mono">{card.embossed_number || '-'}</td>
                <td className="px-3 py-2 font-mono">{card.pin_code || '-'}</td>
                <td className="px-3 py-2">{card.valid_from ? new Date(card.valid_from).toLocaleDateString('sv-SE') : '-'}</td>
                <td className="px-3 py-2">-</td>
                <td className="px-3 py-2">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-950/50 dark:text-green-200">
                    {t('fuel.cards.history.active')}
                  </span>
                </td>
                <td className="px-3 py-2 text-right">
                  <button
                    onClick={() => handleUnlinkCard(card.card_number)}
                    disabled={isUnlinking === card.card_number}
                    className="text-red-600 hover:text-red-800 text-xs underline disabled:opacity-50"
                  >
                    {isUnlinking === card.card_number ? '...' : t('fuel.vehicleCard.unlink')}
                  </button>
                </td>
              </tr>
            ))}
            {historicCards.map(card => (
              <tr key={card.id} className="border-b border-border text-textTertiary">
                <td className="px-3 py-2">
                  {card.fuel_company_name ? (
                    <FuelCompanyIcon companyName={card.fuel_company_name} size="sm" />
                  ) : (
                    <span>-</span>
                  )}
                </td>
                <td className="px-3 py-2">{card.card_name || '-'}</td>
                <td className="px-3 py-2 font-mono">{card.card_number}</td>
                <td className="px-3 py-2 font-mono">{card.embossed_number || '-'}</td>
                <td className="px-3 py-2 font-mono">{card.pin_code || '-'}</td>
                <td className="px-3 py-2">{card.valid_from ? new Date(card.valid_from).toLocaleDateString('sv-SE') : '-'}</td>
                <td className="px-3 py-2">{card.valid_to ? new Date(card.valid_to).toLocaleDateString('sv-SE') : '-'}</td>
                <td className="px-3 py-2">
                  <span className="text-textTertiary text-xs">{t('fuel.cards.history.inactive')}</span>
                </td>
                <td className="px-3 py-2"></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
